// ENG-YAKSON-080 (A7): the FORWARD map that lets the FE gating layer read the
// backend's resolved permission keys (GET /auth/my-permissions) while the nav
// tree + route table still speak in the old flat `feature` vocabulary.
//
// The BACKEND owns the authoritative §8.3 mapping (`permissions.py`
// LEGACY_FEATURE_TO_KEYS, per old cell value). This is its FE mirror, but for a
// different question: not "what grants does this feature seed" but "which keys,
// if the user resolves FULL or OWN on ANY of them, make this feature VISIBLE".
// So each feature maps to the UNION of its FULL-cell and own-cell read keys —
// a Beautician with only `commission.view_own` still sees the Commission nav,
// exactly as the old `commission: 'own'` matrix cell did.
//
// Kept deliberately in sync with the backend registry (permissions.py
// PERMISSION_TREE). If a new gated feature lands, add its keys here.

// The 15-key nav feature vocabulary (was defined inline in navTree.ts). Kept
// here so both navTree and the gating hook read one source.
export type FeatureName =
  | 'dashboard' | 'schedule' | 'customers' | 'staff' | 'treatments' | 'rooms'
  | 'commission' | 'reports' | 'users' | 'dailyReport' | 'leave' | 'salary' | 'finance'
  | 'canBook' | 'inventory';

// The resolved permission keys this FE layer references. Not the full backend
// registry — only the keys that participate in nav/route visibility. Typed as a
// union so a typo in the maps below is a compile error.
export type PermissionKey =
  | 'dashboard.view'
  // DSH-00 / GEN-10 — one key per Dashboard view (backend PERMISSION_TREE).
  // The three HQ views are default-deny (NO_LEGACY_FALLBACK_KEYS); the Outlet
  // view follows `dashboard.view`.
  | 'dashboard.outlet' | 'dashboard.office' | 'dashboard.management' | 'dashboard.business'
  | 'schedule.view' | 'schedule.manage' | 'schedule.view_own'
  // BUG-889: appointments.delete is read directly by the schedule page to gate
  // the Delete affordance; it is deliberately NOT added to FEATURE_TO_KEYS
  // below, because holding it must not by itself make a nav entry visible.
  | 'appointments.book' | 'appointments.delete'
  | 'customers.view' | 'customers.manage'
  | 'staff.view' | 'staff.manage' | 'staff.manage_hr'
  // HR-11-43: Staff Details Report download. Default-deny except ADMIN logins;
  // not in FEATURE_TO_KEYS (it must not reveal a nav entry).
  | 'staff.details_report'
  | 'treatments.view' | 'treatments.manage'
  // Inventory P1/P2. Both write keys are default-deny on the backend
  // (NO_LEGACY_FALLBACK_KEYS), so neither is enough on its own to reveal the nav
  // entry — only `inventory.view` is mapped below. Holding a write key without
  // the read key is a misconfiguration, not a reason to show the menu.
  // `manage_catalogue` = product prices (back office); `manage` = stock
  // quantities, receive/adjust and expiry dates (floor). Deliberately separate.
  | 'inventory.view' | 'inventory.manage_catalogue' | 'inventory.manage'
  | 'rooms.view' | 'rooms.manage'
  // LEA-21: edit Leads message templates. Default-deny except ADMIN logins and HQ
  // staff on the backend; not in FEATURE_TO_KEYS (it must not reveal a nav entry).
  | 'templates.edit'
  // HR › Training › SOP Library. Default-deny on the backend (Admin role only), and
  // the server also requires HQ staff. Gated per-node (navTree anyKey) and per-route
  // (ROUTE_PERMISSIONS), not via FEATURE_TO_KEYS.
  | 'sop.view' | 'sop.manage'
  // Owner 24 Oct 2026: publish / unpublish a meeting's Deck and Minutes files. HQ
  // staff only (the server also checks HQ); default ADMIN logins, no role.
  | 'meetings.publish'
  | 'commission.view' | 'commission.administer' | 'commission.view_own'
  // Fix list F1 (D27-1): approve and pay commission. HQ staff only (the server
  // also checks HQ); default ADMIN logins, otherwise a role or override.
  | 'commission.approve'
  // Request 117: answer the commission breakdown for the staff of one's own
  // outlet. Default ADMIN logins, otherwise a role or override.
  | 'commission.confirm_for_staff'
  | 'reports.view' | 'reports.export'
  | 'users.view' | 'users.manage' | 'users.manage_roles'
  // GEN-15 — Audit trail (backend PERMISSION_TREE `audit`). Not mapped from any legacy
  // feature: only a role / override that grants it (the Admin role by default).
  | 'audit.view'
  | 'dailyReport.view' | 'dailyReport.confirm' | 'dailyReport.view_own'
  | 'leave.request_own' | 'leave.view_all' | 'leave.approve'
  | 'finance.view_all' | 'finance.view_own_salary' | 'finance.manage'
  | 'finance.export' | 'finance.manage_credit_rates' | 'finance.manage_schemes'
  // BUG-867 — POS action gates. Deliberately NOT added to FEATURE_TO_KEYS.finance
  // below: that array drives nav visibility, and holding only finance.edit_gst
  // must not light up the Finance nav item. These are default-deny on the backend
  // (NO_LEGACY_FALLBACK_KEYS) — absence means the control is hidden, not disabled.
  | 'finance.edit_gst' | 'finance.amend_sales_type'
  // Spec 12 — the till key. Also default-deny; gated per-node (navTree anyKey) and
  // per-route (ROUTE_PERMISSIONS['/finance/pos']), NOT via FEATURE_TO_KEYS.finance.
  | 'finance.create_sale'
  // Back-dating an invoice, read directly by the three POS forms to disable the
  // invoice date/time field. Not in FEATURE_TO_KEYS.finance and not a nav gate —
  // a till user without it still reaches every POS screen, they just can't move
  // the date. A sale dated TODAY needs nothing: the backend collapses today's
  // date to `now`, so the disabled field's own value is always accepted.
  | 'finance.backdate_invoice'
  // R5-47 — two-level approvals: Head Master (level 1, OWN = own outlets) and
  // HQ (final). Gated per-node (navTree anyKey), not via FEATURE_TO_KEYS.finance.
  | 'finance.approve_level1' | 'finance.approve_hq'
  // Manage Reports: one view and (where the report downloads) one export key per
  // report — ids in report-registry.ts, mirrored by the backend registry.
  | `report.${string}.view` | `report.${string}.export`
  // Request 118 — per-page open rights (page-registry.ts).
  | `page.${string}.view`;

import { PAGE_REGISTRY, pageViewKey } from './page-registry';
import { REPORT_REGISTRY, reportViewKey } from './report-registry';

// The permission that gates the RBAC settings screens (roles list, role rights,
// user role assignment). Mirrors backend access_service.MANAGE_ROLES_KEY and
// the in-page useManageRolesGate hook.
export const MANAGE_ROLES_KEY: PermissionKey = 'users.manage_roles';

// feature → the resolved keys whose presence (FULL or OWN) makes it visible.
// Visible iff the user has ANY of these keys resolved to a non-NONE level.
export const FEATURE_TO_KEYS: Record<FeatureName, PermissionKey[]> = {
  dashboard: ['dashboard.view'],
  schedule: ['schedule.view', 'schedule.manage', 'schedule.view_own'],
  customers: ['customers.view', 'customers.manage'],
  staff: ['staff.view', 'staff.manage'],
  treatments: ['treatments.view', 'treatments.manage'],
  inventory: ['inventory.view'],
  rooms: ['rooms.view', 'rooms.manage'],
  commission: ['commission.view', 'commission.administer', 'commission.view_own'],
  reports: ['reports.view', 'reports.export'],
  users: ['users.view', 'users.manage', 'users.manage_roles'],
  dailyReport: ['dailyReport.view', 'dailyReport.confirm', 'dailyReport.view_own'],
  leave: ['leave.request_own', 'leave.view_all', 'leave.approve'],
  // Salary has no dedicated legacy feature — the self-view rides on
  // `finance.view_own_salary` (A6, Spec A §6.2) and the admin view on
  // `finance.view_all`. This is the acceptance-critical mapping: a role granting
  // ONLY finance.view_own_salary must light up My Pay and nothing else.
  salary: ['finance.view_all', 'finance.view_own_salary'],
  finance: [
    'finance.view_all',
    'finance.manage',
    'finance.export',
    'finance.manage_credit_rates',
    'finance.manage_schemes',
  ],
  canBook: ['appointments.book'],
};

// Route → required keys (visible iff the user has ANY). Mirrors the shape of the
// legacy ROUTE_FEATURES in permissions.ts. Matched most-specific-first so
// `/settings/roles` (manage_roles) wins over `/settings` (dashboard), and
// `/salary/me` rides on the `/salary` prefix.
export const DASHBOARD_VIEW_KEYS: PermissionKey[] = [
  'dashboard.outlet',
  'dashboard.office',
  'dashboard.management',
  'dashboard.business',
];

// Manage Reports: every report page is gated by its own `report.<id>.view`
// (its default is the gate the page had before). The five-tab /reports page also
// hosts Productivity (?tab=staff), so either key opens it.
const REPORT_ROUTE_PERMISSIONS: Record<string, PermissionKey[]> = {};
for (const [id, entry] of Object.entries(REPORT_REGISTRY)) {
  REPORT_ROUTE_PERMISSIONS[entry.route] = [
    ...(REPORT_ROUTE_PERMISSIONS[entry.route] ?? []),
    reportViewKey(id),
  ];
}

// Request 118: a page's own address opens with its `page.<id>.view` (default =
// the old gate). Addresses shared by several menu pages (/schedule tabs) open
// with any of them, or the old feature key.
const PAGE_ROUTE_PERMISSIONS: Record<string, PermissionKey[]> = {};
for (const [id, entry] of Object.entries(PAGE_REGISTRY)) {
  PAGE_ROUTE_PERMISSIONS[entry.route] = [...(PAGE_ROUTE_PERMISSIONS[entry.route] ?? []), pageViewKey(id)];
}

export const ROUTE_PERMISSIONS: Record<string, PermissionKey[]> = {
  ...REPORT_ROUTE_PERMISSIONS,
  // DSH-00-04: any one view is enough to enter /dashboard/*; the page itself
  // then shows 'no access' on a view the user may not open (a redirect here
  // would hide that the address exists but is closed to them).
  '/dashboard': [...FEATURE_TO_KEYS.dashboard, ...DASHBOARD_VIEW_KEYS],
  // More specific than '/customers', so it wins: the customer reports page is a
  // reporting surface that happens to live under Customers, and its endpoint
  // gates on `reports`. Without this a consultant (customers, no reports) could
  // open a page every request on it 403s.
  '/customers': FEATURE_TO_KEYS.customers,
  '/staff': FEATURE_TO_KEYS.staff,
  '/treatments': FEATURE_TO_KEYS.treatments,
  '/rooms': FEATURE_TO_KEYS.rooms,
  '/commission': FEATURE_TO_KEYS.commission,
  // More specific than '/reports': the Questionnaire Report reads the
  // questionnaire endpoints, gated (and own-scoped) on dailyReport, as its nav item.
  '/daily-report': FEATURE_TO_KEYS.dailyReport,
  '/users': FEATURE_TO_KEYS.users,
  '/leave': FEATURE_TO_KEYS.leave,
  '/salary': FEATURE_TO_KEYS.salary,
  // Spec 12 — the till is reachable by finance.create_sale holders, who may hold
  // NO other finance key. More specific than '/finance', so it wins.
  '/finance/pos': [...FEATURE_TO_KEYS.finance, 'finance.create_sale'],
  // R5-47 — Head Masters reach the approval queue with only their approve key.
  '/finance/approvals': [...FEATURE_TO_KEYS.finance, 'finance.approve_level1', 'finance.approve_hq'],
  // GEN-06 / SAL-27 — Sales › Forms: outlet staff who use the till fill the
  // sales forms in, so the group is open to finance.create_sale holders too.
  // Form Templates and Approvals inside it gate themselves to HQ in-page.
  '/finance/forms': [...FEATURE_TO_KEYS.finance, 'finance.create_sale'],
  '/finance': FEATURE_TO_KEYS.finance,
  // HR → Forms: the submit forms are open to any staff (unmapped → allowed); the
  // submissions review list is HR-only.
  '/hr/forms/submissions': ['staff.manage_hr'],
  // GEN-15 — HR › Manage Company › Audit Trail (HQ staff holding the key).
  '/hr/audit-trail': ['audit.view'],
  // HR › Training › SOP Library — needs sop.view (the server also requires HQ).
  '/training/sop-library': ['sop.view'],
  // Everyone can reach the settings hub (parity with legacy ROUTE_FEATURES,
  // which mapped '/settings' → 'dashboard'). The RBAC sub-screens are the
  // belt-and-suspenders route guard for A8/A9 (which also gate in-page).
  '/settings': FEATURE_TO_KEYS.dashboard,
  '/settings/roles': [MANAGE_ROLES_KEY],
  '/settings/users': [MANAGE_ROLES_KEY],
  // Request 118 — page rights win over the broader entries above.
  ...PAGE_ROUTE_PERMISSIONS,
  // My Pay rides on '/salary' — it stays open to everyone with their own salary
  // right, whatever the Salary page's right says.
  '/salary/me': FEATURE_TO_KEYS.salary,
};

/**
 * Required keys for a pathname (locale already stripped), or `null` when the
 * route is unmapped (caller allows — it will 404 on its own if bogus). Matches
 * the legacy canAccessRoute algorithm: longest matching prefix wins.
 */
export function keysForRoute(pathname: string): PermissionKey[] | null {
  const routeKey = Object.keys(ROUTE_PERMISSIONS)
    .sort((a, b) => b.length - a.length)
    .find((route) => pathname === route || pathname.startsWith(route));

  if (!routeKey) return null;
  return ROUTE_PERMISSIONS[routeKey];
}
